/**
 * Generic owner/member merge primitive.
 *
 * TNCY-6 extracted this from `features/projects/access/useOwnerMemberList.ts`;
 * TNCY-6b moved it to `features/shared/ownership/`; TNCY-9 promotes it into
 * the cross-app `@aeriosiq/ownership` package so the Projects, Contracts,
 * Contract Positions, Inspection Instructions (apps/web) and Data
 * Acquisitions (apps/lyse) merged lists all share ONE merge+dedupe+sort
 * behaviour rather than re-deriving it.
 *
 * v1 merge strategy (OQ-TNCY-6): member rows are bounded-but-complete, own
 * rows are server-paginated; we merge the VISIBLE union (own page ∪ bounded
 * member set), dedupe by a stable id, and client-sort by the active sort
 * key. A heavy-member tenant (member rows > `MEMBER_PAGE_SIZE`) is the
 * escape-hatch case for a future server-merged endpoint — flagged via
 * `memberTruncated`, not solved here.
 *
 * The own-only fast path returns the own items + the own server `total`
 * verbatim, so a tenant with no cross-tenant grants sees exactly the
 * pre-cross-tenant list.
 */

/** Bound on member-scope rows pulled in one shot (the BE caps pageSize at
 *  200). Beyond this the union is incomplete — see OQ-TNCY-6. */
export const MEMBER_PAGE_SIZE = 200;

export interface OwnerMemberMergeInput<R> {
  ownItems: readonly R[];
  /** Server total of OWN rows — drives the footer / pagination. */
  ownTotal: number;
  memberItems: readonly R[];
  /** Server total of MEMBER rows (to detect a truncated bounded fetch). */
  memberTotal: number;
  /** Stable per-row identity for the defensive dedupe. */
  getId: (row: R) => string;
  /** Comparator for the active sort key/dir over the merged union. */
  compare: (a: R, b: R) => number;
}

export interface OwnerMemberMergeResult<R> {
  /** The merged, client-sorted visible union for the current page. */
  rows: R[];
  /** Server total of OWN rows. Member rows are an additive, bounded overlay
   *  and do not inflate the paged total. */
  total: number;
  /** True if ANY member rows were merged in (drives the ownership column). */
  hasMemberRows: boolean;
  /** True when the bounded member fetch may be truncated (OQ-TNCY-6). */
  memberTruncated: boolean;
}

export function mergeOwnerMember<R>({
  ownItems,
  ownTotal,
  memberItems,
  memberTotal,
  getId,
  compare,
}: OwnerMemberMergeInput<R>): OwnerMemberMergeResult<R> {
  // Own-only fast path: no member rows ⇒ return own verbatim (unchanged
  // pre-cross-tenant behaviour).
  if (memberItems.length === 0) {
    return {
      rows: [...ownItems],
      total: ownTotal,
      hasMemberRows: false,
      memberTruncated: false,
    };
  }

  // Merge + client-sort the visible union. Dedupe defensively by id (a row
  // can't be both own + member, but guard anyway).
  const seen = new Set<string>();
  const merged: R[] = [];
  for (const r of [...ownItems, ...memberItems]) {
    const id = getId(r);
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(r);
  }
  merged.sort(compare);

  return {
    rows: merged,
    total: ownTotal,
    hasMemberRows: true,
    memberTruncated: memberTotal > memberItems.length,
  };
}
